import { useState, useCallback } from 'react';
import { Message } from '@/types/chat';

export function useMessageEditing({
  isResponding,
  onStartEdit,
  onSaveEdit,
}: {
  isResponding: boolean;
  onStartEdit: () => void; // To cancel reply, etc.
  onSaveEdit: (message: Message, newContent: string) => Promise<void>;
}) {
  const [isEditingMessage, setIsEditingMessage] = useState<boolean>(false);
  const [messageBeingEdited, setMessageBeingEdited] = useState<Message | null>(null);
  const [editedMessageContent, setEditedMessageContent] = useState<string>('');

  const handleEditMessage = useCallback((message: Message) => {
    if (isResponding) return;
    onStartEdit(); // Notify parent to cancel other states like 'reply'
    setIsEditingMessage(true);
    setMessageBeingEdited(message);
    setEditedMessageContent(message.content);
  }, [isResponding, onStartEdit]);

  const handleCancelEdit = useCallback(() => {
    setIsEditingMessage(false);
    setMessageBeingEdited(null);
    setEditedMessageContent('');
  }, []);

  const handleSaveEdit = useCallback(async () => {
    if (!messageBeingEdited || !editedMessageContent.trim() || editedMessageContent.trim() === messageBeingEdited.content) {
      handleCancelEdit();
      return;
    }
    // The actual resend logic is handled by the passed-in onSaveEdit function
    await onSaveEdit(messageBeingEdited, editedMessageContent);
    handleCancelEdit();
  }, [messageBeingEdited, editedMessageContent, onSaveEdit, handleCancelEdit]);

  return {
    isEditingMessage,
    messageBeingEdited,
    editedMessageContent,
    setEditedMessageContent,
    handleEditMessage,
    handleCancelEdit,
    handleSaveEdit,
  };
}
